import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HardDrive, Loader2, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { getApiHeaders } from '../../utils/apiHelpers';
import { API_ARCHIVIO, mb } from './archivioApi';
import { AzioniSpazio, ProgressoLavoro } from './AzioniSpazio';

const RIGHE = [
  ['liberabili', 'DDT con scansione pronta che si apre correttamente', true],
  ['eliminati_liberi', 'Foto di DDT già cancellati, non usate da nessuna etichetta', true],
  ['senza_scansione', 'Da tenere: DDT senza scansione', false],
  ['scansione_da_rifare', 'Da tenere: scansione da rifare o che non si apre', false],
  ['in_lavorazione', 'Da tenere: scansione in preparazione', false],
  ['in_verifica', 'Controllo ancora in corso: riapri tra un minuto', false],
  ['eliminati_collegati', 'Da tenere: foto di DDT cancellati ancora usate da etichette', false],
];

const Riga = ({ id, testo, libera, voce }) => (
  <div className="flex items-start justify-between gap-3 py-2" data-testid={`spazio-riga-${id}`}>
    <p className={`text-sm ${libera ? 'text-gray-900' : 'text-gray-500'}`}>{testo}</p>
    <p className="text-sm font-semibold whitespace-nowrap tabular-nums">
      <span className="text-gray-500 font-normal">{voce.n} · </span>
      <span className={libera ? 'text-emerald-700' : 'text-gray-600'}>{mb(voce.byte)}</span>
    </p>
  </div>
);

const Riepilogo = ({ d }) => (
  <div className="space-y-4">
    <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="spazio-totale-liberabile">
      <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Si possono liberare</p>
      <p className="text-3xl font-bold text-emerald-800 tabular-nums">{mb(d.liberabili_totale.byte)}</p>
      <p className="text-xs text-emerald-700 mt-0.5">
        {d.liberabili_totale.n} foto su {d.foto_totali.n} ({mb(d.foto_totali.byte)} di foto originali nel database)
      </p>
    </div>
    <div className="divide-y divide-gray-100" data-testid="spazio-righe">
      {RIGHE.filter(([id, , libera]) => libera || d[id].n > 0).map(([id, testo, libera]) => (
        <Riga key={id} id={id} testo={testo} libera={libera} voce={d[id]} />
      ))}
    </div>
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1" data-testid="spazio-nota-collegati">
      {d.collegati_a_etichette > 0 && (
        <p>{d.collegati_a_etichette} DDT con foto sono collegati a etichette e lotti: il link resta e, senza foto, apre la scansione.</p>
      )}
      <p>Le scansioni dell'archivio ({d.scansioni.n}, {mb(d.scansioni.byte)}) restano tutte.</p>
    </div>
  </div>
);

export const LiberaSpazio = () => {
  const [aperto, setAperto] = useState(false);
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(false);
  const [lavoro, setLavoro] = useState(null);

  const carica = useCallback(async (silenzioso = false) => {
    if (!silenzioso) setDati(null);
    setErrore(false);
    try {
      const { data } = await axios.get(`${API_ARCHIVIO}/spazio`, { headers: getApiHeaders(), timeout: 120000 });
      setDati(data);
      setLavoro(data.lavoro?.in_corso ? data.lavoro : null);
    } catch (e) {
      setErrore(true);
    }
  }, []);

  useEffect(() => {
    if (!aperto || !lavoro?.in_corso) return undefined;
    const id = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_ARCHIVIO}/spazio/lavoro`, { headers: getApiHeaders() });
        const l = data.lavoro;
        if (l?.in_corso) {
          setLavoro(l);
          return;
        }
        setLavoro(null);
        const ok = l ? l.fatte - (l.errori || 0) : 0;
        if (l?.tipo === 'ripristina') toast.success(`${ok} foto ripristinate`);
        else if (l?.tipo === 'svuota') toast.success(`${ok} foto eliminate definitivamente`);
        else if (l) toast.success(`Liberati ${mb(l.byte || 0)}: ${ok} foto nel cestino`);
        if (l?.errori) toast.warning(`${l.errori} foto non completate: riprova più tardi`);
        carica();
      } catch (e) {
        console.error('Stato operazione non disponibile', e);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [aperto, lavoro?.in_corso, carica]);

  const apri = () => {
    setAperto(true);
    carica();
  };

  return (
    <>
      <button type="button" onClick={apri} data-testid="archivio-libera-spazio-btn"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors active:scale-95 bg-white/10 text-slate-200 hover:bg-white/20 lg:bg-gray-100 lg:text-gray-700 lg:hover:bg-gray-200">
        <HardDrive className="h-3.5 w-3.5" /> Libera spazio
      </button>
      <Dialog open={aperto} onOpenChange={setAperto}>
        <DialogContent className="max-w-md rounded-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="spazio-dialog">
          <DialogHeader>
            <DialogTitle>Libera spazio</DialogTitle>
            <DialogDescription>
              Le foto originali dei DDT che hanno già la scansione possono uscire dal database: restano nel cestino e, dopo 30 giorni, puoi eliminarle definitivamente.
            </DialogDescription>
          </DialogHeader>
          {errore && (
            <div className="text-center py-6 space-y-3" data-testid="spazio-errore">
              <p className="text-sm text-red-600">Non sono riuscito a calcolare lo spazio.</p>
              <Button variant="outline" onClick={() => carica()} data-testid="spazio-riprova"><RefreshCw className="h-4 w-4 mr-1.5" /> Riprova</Button>
            </div>
          )}
          {!errore && !dati && (
            <div className="flex flex-col items-center gap-2 py-8 text-sm text-gray-500" data-testid="spazio-caricamento">
              <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
              Controllo che ogni scansione si apra correttamente…
            </div>
          )}
          {dati && <Riepilogo d={dati} />}
          {lavoro && <ProgressoLavoro lavoro={lavoro} />}
          {dati && !lavoro && (
            <AzioniSpazio d={dati} onCambiato={() => carica(true)}
              onAvviato={(tipo, totale) => setLavoro({ in_corso: true, tipo, fatte: 0, totale: totale || 0 })} />
          )}
          <Button variant="outline" onClick={() => setAperto(false)} className="w-full" data-testid="spazio-chiudi">Chiudi</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
